import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AppData } from '../api/client';
import {
	deleteMessages,
	fetchFolders,
	fetchMessage,
	fetchMessageList,
	moveMessages,
	setFlagged,
	setSeen
} from '../api/mail';
import type { Folder, Message, MessageSummary } from '../api/types';
import { isUnread } from '../api/types';
import { buildMailboxes, findView, STARRED, type FolderSettings, type NavItem } from '../mail/mailboxes';
import { usePendingAction } from '../state/usePendingAction';
import { FolderList } from './mail/FolderList';
import { MessageList, type BulkAction } from './mail/MessageList';
import { MessageView } from './mail/MessageView';
import {
	Composer,
	emptyDraft,
	forwardDraft,
	replyDraft,
	type Draft
} from './mail/Composer';
import { NotificationPrompt } from './mail/NotificationPrompt';
import { SettingsScreen } from './SettingsScreen';
import { Button } from '../ui';
import { OnboardingWizard } from './OnboardingWizard';

const PAGE = 50;

/**
 * Coming back to the tab refreshes the open folder, but not if it was loaded
 * this recently -- flicking between apps should not cost an IMAP round trip
 * each time. This is a reaction to the user, not a timer: there is no polling.
 */
const REFRESH_AFTER = 30_000;

/**
 * Adds or removes one IMAP flag.
 *
 * Case-insensitive on removal because servers differ on how they spell flags
 * back, and a case-sensitive filter would leave "\\seen" in place while the UI
 * showed the message as unread.
 */
function nextFlags(flags: string[] | undefined, flag: string, present: boolean): string[] {
	const without = (flags ?? []).filter(item => item.toLowerCase() !== flag.toLowerCase());
	return present ? [...without, flag] : without;
}

/** Newest first, the order the list is fetched in. Used when putting rows back. */
const byDate = (a: MessageSummary, b: MessageSummary) => b.dateTimestamp - a.dateTimestamp;

function emptyTextFor(view: NavItem, roles: ReturnType<typeof buildMailboxes>['roles']): string {
	if (STARRED === view.id) {
		return 'No starred messages. Tap ☆ next to a message to find it here later.';
	}
	switch (view.folder) {
		case roles.inbox:
			return 'You’re all caught up. Nothing in your Inbox.';
		case roles.sent:
			return 'Nothing sent yet.';
		case roles.drafts:
			return 'No drafts.';
		case roles.junk:
			return 'No spam. Nice.';
		case roles.trash:
			return 'Trash is empty.';
		case roles.archive:
			return 'Nothing archived yet. Archive moves mail out of your Inbox without deleting it.';
	}
	return 'Nothing here.';
}

/** True while the user is typing somewhere, when single-key shortcuts must stay out of the way. */
function isTyping(target: EventTarget | null): boolean {
	const element = target as HTMLElement | null;
	if (!element) {
		return false;
	}
	return (
		'INPUT' === element.tagName ||
		'TEXTAREA' === element.tagName ||
		'SELECT' === element.tagName ||
		element.isContentEditable
	);
}

type Pane = 'folders' | 'list' | 'reading';

export function MailScreen({ session, onSignOut }: { session: AppData; onSignOut: () => void }) {
	const email = String(session.Email ?? '');

	const [folders, setFolders] = useState<Folder[]>([]);
	const [viewId, setViewId] = useState('INBOX');
	const [messages, setMessages] = useState<MessageSummary[]>([]);
	const [total, setTotal] = useState(0);
	const [offset, setOffset] = useState(0);
	const [search, setSearch] = useState('');
	const [listLoading, setListLoading] = useState(false);
	const [listError, setListError] = useState('');
	const [selected, setSelected] = useState<Set<number>>(new Set());

	const [openSummary, setOpenSummary] = useState<MessageSummary | null>(null);
	const [message, setMessage] = useState<Message | null>(null);
	const [messageLoading, setMessageLoading] = useState(false);
	const [messageError, setMessageError] = useState('');

	const [draft, setDraft] = useState<Draft | null>(null);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [pane, setPane] = useState<Pane>('list');
	const [notice, setNotice] = useState('');

	// Shown over the mailbox until enrollment is done. The wizard decides for
	// itself whether there is anything to do and calls back immediately when
	// there is not, so it never flashes up for an existing user.
	const [onboarding, setOnboarding] = useState(true);

	// The account's own folder settings (SentFolder etc. in app data) come first:
	// they are what the server falls back to, so the two never disagree.
	const mailboxes = useMemo(
		() => buildMailboxes(folders, session as unknown as FolderSettings),
		[folders, session]
	);
	const { roles } = mailboxes;
	const view = findView(mailboxes, viewId);

	// Where a sent copy is filed. Never empty: an empty saveFolder tells the server
	// to keep NO copy, so anything sent before the folder list had loaded (or when
	// it failed to) vanished from the user's view. If "Sent" turns out not to
	// exist, the server retries the account's configured Sent folder itself.
	const configuredSent = String(session.SentFolder ?? '') || undefined;
	const saveSentTo = roles.sent ?? configuredSent ?? 'Sent';

	const pending = usePendingAction(label =>
		setNotice(`That didn’t go through (${label.toLowerCase()}). The messages are back where they were.`)
	);

	/* ------------------------------------------------------------- folders */

	const loadFolders = useCallback(async () => {
		try {
			const collection = await fetchFolders();
			setFolders(collection['@Collection'] ?? []);
		} catch {
			// A folder list failure is not fatal -- the Inbox default still works,
			// and surfacing it here would bury the message list behind an error
			// about the sidebar.
		}
	}, []);

	useEffect(() => {
		void loadFolders();
	}, [loadFolders]);

	// Gmail's "(3) Inbox" in the tab title: the one number worth seeing from
	// another tab.
	const inboxUnread = mailboxes.primary[0]?.count ?? 0;
	useEffect(() => {
		document.title = inboxUnread ? `(${inboxUnread}) Keystone Mail` : 'Keystone Mail';
	}, [inboxUnread]);

	/* -------------------------------------------------------- message list */

	// Guards against an older, slower request overwriting a newer one -- easy to
	// trigger by clicking through folders quickly.
	const listRequest = useRef(0);
	const lastLoaded = useRef(0);

	const loadList = useCallback(
		async (folder: string, filter: string, nextOffset: number, term: string, append: boolean) => {
			const ticket = ++listRequest.current;
			lastLoaded.current = Date.now();
			setListLoading(true);
			setListError('');

			try {
				const result = await fetchMessageList({
					folder,
					offset: nextOffset,
					limit: PAGE,
					search: [filter, term.trim()].filter(Boolean).join(' ')
				});
				if (ticket !== listRequest.current) {
					return;
				}
				const rows = result['@Collection'] ?? [];
				setMessages(previous => (append ? [...previous, ...rows] : rows));
				setTotal(result.totalEmails ?? rows.length);
				setOffset(nextOffset);
			} catch {
				if (ticket !== listRequest.current) {
					return;
				}
				setListError('Could not load this folder. Check your connection and tap ↻ to try again.');
				if (!append) {
					setMessages([]);
				}
			} finally {
				if (ticket === listRequest.current) {
					setListLoading(false);
				}
			}
		},
		[]
	);

	const viewFolder = view.folder;
	const viewFilter = view.filter ?? '';
	useEffect(() => {
		setSelected(new Set());
		void loadList(viewFolder, viewFilter, 0, search, false);
	}, [viewFolder, viewFilter, search, loadList]);

	// Search is debounced so typing does not fire an IMAP search per keystroke.
	const [searchInput, setSearchInput] = useState('');
	useEffect(() => {
		const timer = setTimeout(() => setSearch(searchInput), 300);
		return () => clearTimeout(timer);
	}, [searchInput]);

	// Read through refs by the listeners below, which are attached once.
	const viewRef = useRef(view);
	const searchRef = useRef(search);
	viewRef.current = view;
	searchRef.current = search;

	/** Re-reads the open view and the counts. */
	const refresh = useCallback(() => {
		void loadFolders();
		void loadList(viewRef.current.folder, viewRef.current.filter ?? '', 0, searchRef.current, false);
	}, [loadFolders, loadList]);

	// New mail shows up without anyone having to reload the page -- still
	// without polling. Two triggers, both driven by something actually happening:
	//  - coming back to the app (tab focus, phone unlocked, app reopened), and
	//  - the service worker relaying a push from the IMAP IDLE watcher while
	//    the app is open.
	useEffect(() => {
		const onReturn = () => {
			if ('visible' === document.visibilityState && Date.now() - lastLoaded.current > REFRESH_AFTER) {
				refresh();
			}
		};
		const onWorkerMessage = () => refresh();

		document.addEventListener('visibilitychange', onReturn);
		window.addEventListener('focus', onReturn);
		navigator.serviceWorker?.addEventListener('message', onWorkerMessage);
		return () => {
			document.removeEventListener('visibilitychange', onReturn);
			window.removeEventListener('focus', onReturn);
			navigator.serviceWorker?.removeEventListener('message', onWorkerMessage);
		};
	}, [refresh]);

	const selectView = useCallback(
		(item: NavItem) => {
			// Tapping the view you are already in is how people ask for it to be
			// refreshed. Setting the same id is a no-op in React, so without this it
			// did nothing at all.
			if (item.id === viewRef.current.id) {
				refresh();
			}
			setViewId(item.id);
			setSearchInput('');
			setOpenSummary(null);
			setMessage(null);
			setPane('list');
		},
		[refresh]
	);

	/* ------------------------------------------------------------- reading */

	const openMessage = useCallback(async (summary: MessageSummary) => {
		setOpenSummary(summary);
		setPane('reading');
		setMessage(null);
		setMessageError('');
		setMessageLoading(true);

		try {
			const full = await fetchMessage(summary.folder, summary.uid);
			setMessage(full);

			if (isUnread(summary)) {
				// Optimistic: the row updates immediately and the server catches up.
				// A failure here is not worth interrupting reading for.
				setMessages(rows =>
					rows.map(row =>
						row.uid === summary.uid && row.folder === summary.folder
							? { ...row, flags: nextFlags(row.flags, '\\Seen', true) }
							: row
					)
				);
				setFolders(list =>
					list.map(folder =>
						folder.fullName === summary.folder
							? { ...folder, unreadEmails: Math.max(0, folder.unreadEmails - 1) }
							: folder
					)
				);
				void setSeen(summary.folder, [summary.uid], true).catch(() => {});
			}
		} catch {
			setMessageError('Could not open this message. It may have been moved or deleted elsewhere.');
		} finally {
			setMessageLoading(false);
		}
	}, []);

	const closeReading = useCallback(() => {
		setOpenSummary(null);
		setMessage(null);
		setPane('list');
	}, []);

	/* ------------------------------------------------ moving, with undo */

	/**
	 * Takes rows out of the list now and moves them on the server a few seconds
	 * later, unless Undo is pressed -- see usePendingAction.
	 */
	const moveWithUndo = useCallback(
		(uids: number[], from: string, to: string, label: string) => {
			if (!uids.length) {
				return;
			}
			const wanted = new Set(uids);
			let removed: MessageSummary[] = [];
			setMessages(rows => {
				removed = rows.filter(row => wanted.has(row.uid));
				return rows.filter(row => !wanted.has(row.uid));
			});
			// Keep the count in step, or "Load more" appears for rows that are gone.
			setTotal(count => Math.max(0, count - uids.length));
			setSelected(new Set());
			if (openSummary && wanted.has(openSummary.uid)) {
				closeReading();
			}

			pending.schedule(
				label,
				async () => {
					await moveMessages(from, to, uids);
					void loadFolders();
				},
				() => {
					setMessages(rows => [...rows, ...removed].sort(byDate));
					setTotal(count => count + removed.length);
				}
			);
		},
		[openSummary, closeReading, pending, loadFolders]
	);

	const archive = useCallback(
		(uids: number[]) => {
			if (roles.archive) {
				moveWithUndo(uids, viewRef.current.folder, roles.archive, uids.length > 1 ? `${uids.length} archived` : 'Archived');
			}
		},
		[roles.archive, moveWithUndo]
	);

	const markSpam = useCallback(
		(uids: number[]) => {
			if (roles.junk) {
				moveWithUndo(uids, viewRef.current.folder, roles.junk, 'Moved to Spam');
			}
		},
		[roles.junk, moveWithUndo]
	);

	const remove = useCallback(
		async (uids: number[]) => {
			const folder = viewRef.current.folder;
			if (roles.trash && roles.trash !== folder) {
				moveWithUndo(uids, folder, roles.trash, uids.length > 1 ? `${uids.length} moved to Trash` : 'Moved to Trash');
				return;
			}
			// Already in Trash (or there is no Trash): this one really is permanent,
			// so it asks instead of offering an undo it could not honour.
			const count = uids.length > 1 ? `these ${uids.length} messages` : 'this message';
			if (!window.confirm(`Delete ${count} forever? This can’t be undone.`)) {
				return;
			}
			const wanted = new Set(uids);
			setMessages(rows => rows.filter(row => !wanted.has(row.uid)));
			setTotal(count => Math.max(0, count - uids.length));
			setSelected(new Set());
			if (openSummary && wanted.has(openSummary.uid)) {
				closeReading();
			}
			try {
				await deleteMessages(folder, uids, null);
			} catch {
				setNotice('Could not delete. Please try again.');
				refresh();
			}
			void loadFolders();
		},
		[roles.trash, moveWithUndo, openSummary, closeReading, refresh, loadFolders]
	);

	const moveTo = useCallback(
		(target: Message, destination: string) => {
			const name = [...mailboxes.primary, ...mailboxes.more].find(item => item.folder === destination)?.label ?? destination;
			const label =
				destination === roles.archive ? 'Archived' : destination === roles.junk ? 'Moved to Spam' : `Moved to ${name}`;
			moveWithUndo([target.uid], target.folder, destination, label);
		},
		[mailboxes, roles.archive, roles.junk, moveWithUndo]
	);

	/* --------------------------------------------------- flags (instant) */

	const setStar = useCallback(
		async (folder: string, uids: number[], starred: boolean) => {
			const wanted = new Set(uids);
			setMessage(current =>
				current && wanted.has(current.uid) ? { ...current, flags: nextFlags(current.flags, '\\Flagged', starred) } : current
			);
			setMessages(rows =>
				rows.map(row => (wanted.has(row.uid) ? { ...row, flags: nextFlags(row.flags, '\\Flagged', starred) } : row))
			);
			try {
				await setFlagged(folder, uids, starred);
			} catch {
				refresh();
			}
		},
		[refresh]
	);

	const setRead = useCallback(
		async (folder: string, uids: number[], read: boolean) => {
			const wanted = new Set(uids);
			let changed = 0;
			setMessages(rows =>
				rows.map(row => {
					if (!wanted.has(row.uid) || isUnread(row) === !read) {
						return row;
					}
					changed += 1;
					return { ...row, flags: nextFlags(row.flags, '\\Seen', read) };
				})
			);
			setFolders(list =>
				list.map(item =>
					item.fullName === folder
						? { ...item, unreadEmails: Math.max(0, item.unreadEmails + (read ? -changed : changed)) }
						: item
				)
			);
			try {
				await setSeen(folder, uids, read);
			} catch {
				refresh();
			}
		},
		[refresh]
	);

	const markUnread = useCallback(
		(target: Message) => {
			// Back to the list: staying in the reading pane on a message now marked
			// unread reads as though the action did not take.
			closeReading();
			void setRead(target.folder, [target.uid], false);
		},
		[closeReading, setRead]
	);

	/* ----------------------------------------------------------- selection */

	const toggleSelect = useCallback((uid: number) => {
		setSelected(current => {
			const next = new Set(current);
			next.has(uid) ? next.delete(uid) : next.add(uid);
			return next;
		});
	}, []);

	const onBulk = useCallback(
		(action: BulkAction) => {
			const uids = [...selected];
			const folder = viewRef.current.folder;
			switch (action) {
				case 'archive':
					return archive(uids);
				case 'spam':
					return markSpam(uids);
				case 'delete':
					return void remove(uids);
				case 'read':
				case 'unread':
					setSelected(new Set());
					return void setRead(folder, uids, 'read' === action);
			}
		},
		[selected, archive, markSpam, remove, setRead]
	);

	/* ------------------------------------------------------------ composing */

	const startReply = useCallback(
		(target: Message, all: boolean) => setDraft(replyDraft(target, all, email)),
		[email]
	);

	const onSent = useCallback(
		(warning?: string) => {
			setDraft(null);
			setNotice(warning ?? '');
			// Refresh whatever is open. The list used to stay as it was, so sending
			// from the Sent folder -- or to yourself from the Inbox -- showed nothing
			// new until a full page reload.
			refresh();
		},
		[refresh]
	);

	/* ---------------------------------------------------- keyboard shortcuts */

	// Gmail's keys, for people who live in the app on a desktop. Listed in
	// Settings. Single keys only, and never while typing or composing.
	const keyState = useRef({ messages, openSummary, message, draft, selected, archive, remove, setStar, startReply, openMessage, closeReading });
	keyState.current = { messages, openSummary, message, draft, selected, archive, remove, setStar, startReply, openMessage, closeReading };

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			const s = keyState.current;
			if (event.ctrlKey || event.metaKey || event.altKey || isTyping(event.target) || s.draft) {
				return;
			}
			const index = s.openSummary ? s.messages.findIndex(row => row.uid === s.openSummary!.uid) : -1;
			const step = (delta: number) => {
				const next = s.messages[Math.max(0, Math.min(s.messages.length - 1, index + delta))];
				next && void s.openMessage(next);
			};

			switch (event.key) {
				case 'c':
					setDraft(emptyDraft());
					break;
				case '/':
					document.querySelector<HTMLInputElement>('[data-shortcut="search"]')?.focus();
					break;
				case 'j':
					step(index < 0 ? 0 : 1);
					break;
				case 'k':
					step(index < 0 ? 0 : -1);
					break;
				case 'e':
					s.message && s.archive([s.message.uid]);
					break;
				case '#':
					s.message && void s.remove([s.message.uid]);
					break;
				case 's':
					s.message && void s.setStar(s.message.folder, [s.message.uid], !s.message.flags?.some(f => '\\flagged' === f.toLowerCase()));
					break;
				case 'r':
					s.message && s.startReply(s.message, false);
					break;
				case 'a':
					s.message && s.startReply(s.message, true);
					break;
				case 'f':
					s.message && setDraft(forwardDraft(s.message));
					break;
				case 'u':
				case 'Escape':
					if (s.selected.size) {
						setSelected(new Set());
					} else {
						s.closeReading();
					}
					break;
				default:
					return;
			}
			event.preventDefault();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);

	/* ---------------------------------------------------------------- view */

	if (settingsOpen) {
		return (
			<SettingsScreen
				email={email}
				// Supplied by the keystone-push plugin through filter.app-data. Absent
				// when push is not configured on the server, which the settings screen
				// reports rather than offering a toggle that cannot work.
				pushKey={session.KeystonePushKey as string | undefined}
				workerUrl={session.KeystonePushWorker as string | undefined}
				onBack={() => setSettingsOpen(false)}
				onSignOut={onSignOut}
			/>
		);
	}

	if (onboarding) {
		return <OnboardingWizard onFinish={() => setOnboarding(false)} />;
	}

	const inSentOrDrafts = view.folder === roles.sent || view.folder === roles.drafts;

	return (
		<>
			<div className="app" data-pane={pane}>
				<FolderList
					mailboxes={mailboxes}
					current={view.id}
					email={email}
					onSelect={selectView}
					onCompose={() => setDraft(emptyDraft())}
					onSettings={() => setSettingsOpen(true)}
					onSignOut={onSignOut}
				/>

				<MessageList
					title={view.label}
					messages={messages}
					openUid={openSummary?.uid ?? null}
					selected={selected}
					loading={listLoading}
					error={listError}
					total={total}
					search={searchInput}
					searchPlaceholder={`Search ${view.label}`}
					emptyText={emptyTextFor(view, roles)}
					// In Sent and Drafts every message is from you; who it went TO is the
					// only useful thing to show.
					showRecipients={inSentOrDrafts}
					canArchive={Boolean(roles.archive) && view.folder !== roles.archive}
					canSpam={Boolean(roles.junk) && view.folder !== roles.junk && !inSentOrDrafts}
					onSearch={setSearchInput}
					onOpen={summary => void openMessage(summary)}
					onToggleSelect={toggleSelect}
					onSelectAll={() => setSelected(new Set(messages.map(row => row.uid)))}
					onClearSelection={() => setSelected(new Set())}
					onBulk={onBulk}
					onStar={(summary, starred) => void setStar(summary.folder, [summary.uid], starred)}
					onLoadMore={() => void loadList(view.folder, view.filter ?? '', offset + PAGE, search, true)}
					onMenu={() => setPane('folders')}
					onRefresh={refresh}
					banner={
						<>
							<NotificationPrompt
								pushKey={session.KeystonePushKey as string | undefined}
								workerUrl={session.KeystonePushWorker as string | undefined}
							/>
							{notice && (
								<div className="notice" role="status">
									<p className="notice__text">{notice}</p>
									<div className="notice__actions">
										<Button size="sm" variant="ghost" onClick={() => setNotice('')}>
											Dismiss
										</Button>
									</div>
								</div>
							)}
						</>
					}
				/>

				<MessageView
					message={message}
					loading={messageLoading}
					error={messageError}
					folders={folders}
					archiveFolder={roles.archive}
					junkFolder={roles.junk}
					onBack={closeReading}
					onReply={startReply}
					onForward={target => setDraft(forwardDraft(target))}
					onDelete={target => void remove([target.uid])}
					onFlag={(target, flagged) => void setStar(target.folder, [target.uid], flagged)}
					onUnread={markUnread}
					onMove={moveTo}
				/>
			</div>

			{/* Phones: Gmail's floating Compose button, always within thumb reach. */}
			{'list' === pane && !draft && (
				<button className="fab" onClick={() => setDraft(emptyDraft())} aria-label="Compose">
					✎ Compose
				</button>
			)}

			{pending.label && (
				<div className="toast" role="status">
					<span>{pending.label}</span>
					<button className="toast__action" onClick={pending.undo}>
						Undo
					</button>
				</div>
			)}

			{draft && (
				<Composer
					draft={draft}
					from={email}
					sentFolder={saveSentTo}
					draftsFolder={roles.drafts}
					onClose={() => setDraft(null)}
					onSent={onSent}
				/>
			)}
		</>
	);
}
